import { CloseOutlined } from '@mui/icons-material';
import { Button } from 'finara-components';
import { useState } from 'react';

import { Input } from '@/components/common/Input';

interface AddDepartmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; description?: string }) => void;
  isLoading?: boolean;
}

export const AddDepartmentModal: React.FC<AddDepartmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<{ name?: string }>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { name?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'El nombre del departamento es requerido';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
    });

    setName('');
    setDescription('');
    setErrors({});
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black opacity-60 cursor-default"
        onClick={handleClose}
        type="button"
        onKeyDown={e => e.key === 'Escape' && handleClose()}
        aria-label="Cerrar modal"
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-2xl bg-surface border border-black/10 dark:border-white/15 rounded-lg shadow-2xl shadow-black/60">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-xl font-semibold text-brand-white">Crear Nuevo Departamento</h2>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 text-muted hover:text-brand-white hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            disabled={isLoading}
          >
            <CloseOutlined />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 px-6 py-4">
            <Input
              id="department-name"
              label="Nombre del Departamento"
              type="text"
              value={name}
              onChange={e => {
                setName(e.target.value);
                if (errors.name) setErrors({ ...errors, name: undefined });
              }}
              placeholder="Ej: Ingeniería, Recursos Humanos, Ventas"
              error={errors.name}
              required
              disabled={isLoading}
            />

            <div>
              <label
                htmlFor="department-description"
                className="mb-1 block text-sm font-medium text-brand-white"
              >
                Descripción (Opcional)
              </label>
              <textarea
                id="department-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe las responsabilidades y funciones de este departamento..."
                rows={4}
                disabled={isLoading}
                className="w-full rounded-lg border border-border bg-surface text-brand-white placeholder:text-muted px-4 py-2 text-sm focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 p-3 text-sm">
              <p className="mb-1 font-medium text-brand-white">Información</p>
              <p className="text-muted">
                Los departamentos ayudan a organizar a los empleados y facilitan la gestión de la
                estructura organizacional.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
            <Button type="button" variant="white" onClick={handleClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="black" disabled={isLoading}>
              {isLoading ? 'Creando...' : 'Crear Departamento'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
